import { Split } from 'lucide-react';

import { Callout } from '@/_shared/components/ui/callout';
import { InputGroup, InputGroupInput } from '@/_shared/components/ui/input-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { cn } from '@/_shared/lib/utils';

import { hasStepPreview } from '../lib/step-preview';
import type { Condition, ConditionOp, StepStatus, TestRunResult } from '../types';
import { StepCard } from './StepCard';
import { StepResult } from './StepResult';

/** Comparison operators — static enum, no server call needed. */
const OPERATORS: readonly { value: ConditionOp; label: string }[] = [
  { value: '>', label: 'greater than' },
  { value: '>=', label: 'at least' },
  { value: '<', label: 'less than' },
  { value: '<=', label: 'at most' },
  { value: '==', label: 'equals' },
  { value: '!=', label: 'not equal to' },
];

/** Seeded when the switch is turned on (v0 supports only the `row_count` lhs). */
const DEFAULT_CONDITION: Condition = { column: 'row_count', op: '>', value: '0' };

/** Column/operator selects sit inside one InputGroup pill — they drop their own frame so the group owns the border + focus ring. */
// `!` on the shadow-none: twMerge can't dedupe the base trigger's theme shadows (shadow-secondary/-primary)
// against shadow-none, so without it the segment's own grey-hover / blue-focus glow leaks over the group ring.
const SEAMLESS_TRIGGER =
  'h-full rounded-none border-0 bg-transparent shadow-none hover:shadow-none! focus:border-transparent focus:shadow-none! disabled:bg-transparent';

interface ConditionStepProps {
  stepNumber: number;
  /** `null` = the switch is off → "Send always". A value = "Send only if…". */
  condition: Condition | null;
  status: StepStatus;
  result: NonNullable<TestRunResult['condition']> | null;
  /** Dry-run error message to display inline (BAD_REQUEST from server). */
  error?: string | null;
  /** Emits the next condition, or `null` when the switch is turned off. */
  onChange: (next: Condition | null) => void;
  /** Form-validation error for the threshold value — shown inline in danger once touched/submitted. */
  valueError?: string;
  /** Mark the threshold field touched (on blur) so its error may show. */
  onValueBlur?: () => void;
  /** Read-only while the agent is mid-turn (it owns the draft); the human edits once it's idle (v2 §0). */
  readOnly?: boolean;
  /** Flash the card's highlight when the agent freshly fills this step (§6.5). */
  highlight?: boolean;
}

export function ConditionStep({
  stepNumber,
  condition,
  status,
  result,
  error,
  onChange,
  valueError,
  onValueBlur,
  readOnly = false,
  highlight = false,
}: ConditionStepProps): React.ReactElement {
  return (
    <StepCard
      stepNumber={stepNumber}
      kind="Condition"
      title="When to send"
      icon={Split}
      highlight={highlight}
      hasError={!!error}
      footerStale={status === 'stale'}
      action={
        <Select
          value={condition ? 'conditional' : 'always'}
          onValueChange={(v) => onChange(v === 'conditional' ? DEFAULT_CONDITION : null)}
          disabled={readOnly}
        >
          <SelectTrigger aria-label="When to send" className="w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="always">Always</SelectItem>
            <SelectItem value="conditional">Only if…</SelectItem>
          </SelectContent>
        </Select>
      }
      footer={
        // "Always" (no condition) has nothing to preview — not even while a run is in flight.
        condition != null && hasStepPreview(status, result) ? (
          <StepResult status={status}>
            {result != null &&
              (result.passes ? (
                <p className="text-[0.8125rem] font-medium text-success">
                  ✓ Notification will be sent — {result.summary}
                </p>
              ) : (
                <p className="text-[0.8125rem] font-medium text-danger">
                  ✕ Notification won't be sent — condition not met
                </p>
              ))}
          </StepResult>
        ) : undefined
      }
    >
      {condition ? (
        <>
          {/* Column · operator · value combined into one field — reads as "row_count greater than 0". */}
          <InputGroup size="md" aria-label="Condition" className="w-fit">
            {/* v0: only row_count is supported as the lhs column. */}
            <Select
              value={condition.column}
              onValueChange={(column) => onChange({ ...condition, column })}
              disabled={readOnly}
            >
              <SelectTrigger aria-label="Column" className={cn(SEAMLESS_TRIGGER, 'font-mono')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="row_count" className="font-mono">
                  row_count
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Operator — text labels only (no comparison symbols). The value stays the ConditionOp. */}
            <Select
              value={condition.op}
              onValueChange={(op) => onChange({ ...condition, op: op as ConditionOp })}
              disabled={readOnly}
            >
              <SelectTrigger aria-label="Operator" className={SEAMLESS_TRIGGER}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPERATORS.map((op) => (
                  <SelectItem key={op.value} value={op.value}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* row_count is a non-negative integer, so strip anything but digits (no decimals/negatives). */}
            <InputGroupInput
              id="condition-value"
              aria-label="Value"
              inputMode="numeric"
              value={condition.value}
              onChange={(e) => onChange({ ...condition, value: e.target.value.replace(/[^0-9]/g, '') })}
              // Reject a paste that isn't a clean non-negative integer instead of silently mangling it —
              // digit-stripping would turn a pasted "12.5" into "125" (a wrong threshold the user never
              // typed), then Save it. Only whole-number pastes are allowed through to the change handler.
              onPaste={(e) => {
                if (!/^\d+$/.test(e.clipboardData.getData('text').trim())) e.preventDefault();
              }}
              onBlur={onValueBlur}
              disabled={readOnly}
              aria-invalid={!!valueError}
              className="w-16 flex-none font-mono"
            />
          </InputGroup>

          {valueError ? (
            <p className="text-xs font-medium text-danger" role="alert">
              {valueError} — the routine won't save or run until it's set.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Only send when this is true — otherwise nothing is sent.</p>
          )}

          {/* role="alert" so the step-specific dry-run reason is announced (the top banner is only a count). */}
          {error && (
            <Callout variant="error" role="alert">
              {error}
            </Callout>
          )}
        </>
      ) : null}
    </StepCard>
  );
}
